import React, { useState } from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import {
  FaArrowLeft,
  FaSave,
  FaTimes,
  FaTruck,
  FaMapMarkerAlt,
  FaUser,
  FaBox,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaShieldAlt,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { api } from "../../services/authServices";
import ConfirmModal from "../../components/ConfirmModal";

const CreateShipment = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");

  // Pre-submit reminder - nothing is required, this never blocks submission
  const [missingFields, setMissingFields] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    originAddress: {
      street: "",
      city: "",
      state: "",
      postalCode: "",
      country: "USA",
    },
    destinationAddress: {
      street: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
    },
    sender: {
      name: "",
      company: "",
      email: "",
      phone: "",
    },
    receiver: {
      name: "",
      company: "",
      email: "",
      phone: "",
    },
    weight: {
      value: "",
      unit: "kg",
    },
    dimensions: {
      length: "",
      width: "",
      height: "",
      unit: "cm",
    },
    description: "",
    estimatedDaysInTransit: "",
    status: "Packaging",
    serviceType: "Standard",
    shippingCost: "",
    insurance: "",
    isFragile: false,
    specialInstructions: "",
  });

  const handleAddressChange = (type, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value,
      },
    }));
  };

  const handlePersonChange = (type, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value,
      },
    }));
  };

  const handleWeightChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      weight: {
        ...prev.weight,
        [field]: value,
      },
    }));
  };

  const handleDimensionsChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      dimensions: {
        ...prev.dimensions,
        [field]: value,
      },
    }));
  };

  // Nothing is required to create a shipment - this just tracks what's
  // blank so we can remind the admin before submitting.
  const getMissingFields = () => {
    const missing = [];
    const o = formData.originAddress;
    const d = formData.destinationAddress;

    if (!o.street) missing.push("Origin Street Address");
    if (!o.city) missing.push("Origin City");
    if (!o.state) missing.push("Origin State");
    if (!o.postalCode) missing.push("Origin Postal Code");
    if (!o.country) missing.push("Origin Country");

    if (!d.street) missing.push("Destination Street Address");
    if (!d.city) missing.push("Destination City");
    if (!d.state) missing.push("Destination State");
    if (!d.postalCode) missing.push("Destination Postal Code");
    if (!d.country) missing.push("Destination Country");

    if (!formData.sender.name) missing.push("Sender Name");
    if (!formData.sender.company) missing.push("Sender Company");
    if (!formData.sender.email) missing.push("Sender Email");
    if (!formData.sender.phone) missing.push("Sender Phone");

    if (!formData.receiver.name) missing.push("Receiver Name");
    if (!formData.receiver.company) missing.push("Receiver Company");
    if (!formData.receiver.email) missing.push("Receiver Email");
    if (!formData.receiver.phone) missing.push("Receiver Phone");

    if (!formData.weight.value) missing.push("Package Weight");
    if (!formData.dimensions.length) missing.push("Package Length");
    if (!formData.dimensions.width) missing.push("Package Width");
    if (!formData.dimensions.height) missing.push("Package Height");
    if (!formData.description) missing.push("Package Description");
    if (!formData.estimatedDaysInTransit)
      missing.push("Estimated Days in Transit");
    if (!formData.shippingCost) missing.push("Shipping Cost");
    if (!formData.insurance) missing.push("Insurance");
    if (!formData.specialInstructions) missing.push("Special Instructions");

    return missing;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setFormError("");
    setFormSuccess("");

    const missing = getMissingFields();
    if (missing.length > 0) {
      setMissingFields(missing);
      return;
    }

    createShipment();
  };

  const createShipment = async () => {
    setMissingFields([]);
    setIsSubmitting(true);
    setFormError("");
    setFormSuccess("");

    try {
      const response = await api.post("/api/user/shipment/create-shipment", {
        ...formData,
        weight: {
          ...(formData.weight.value !== "" && {
            value: parseFloat(formData.weight.value),
          }),
          unit: formData.weight.unit,
        },
        dimensions: {
          length: formData.dimensions.length
            ? parseFloat(formData.dimensions.length)
            : 0,
          width: formData.dimensions.width
            ? parseFloat(formData.dimensions.width)
            : 0,
          height: formData.dimensions.height
            ? parseFloat(formData.dimensions.height)
            : 0,
          unit: formData.dimensions.unit,
        },
        estimatedDaysInTransit:
          formData.estimatedDaysInTransit !== ""
            ? parseInt(formData.estimatedDaysInTransit)
            : undefined,
        shippingCost: formData.shippingCost
          ? parseFloat(formData.shippingCost)
          : 0,
        insurance: formData.insurance ? parseFloat(formData.insurance) : 0,
      });

      if (response.data.success) {
        setFormSuccess("Shipment created successfully!");
        setTimeout(() => {
          navigate("/admin-dashboard");
        }, 2000);
      } else {
        setFormError(response.data.message || "Failed to create shipment");
      }
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || "Something went wrong";
      setFormError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Create Shipment | Admin Dashboard</title>
      </Helmet>

      <PageWrapper>
        <Container>
          {/* Header */}
          <Header>
            <BackButton onClick={() => navigate("/admin-dashboard")}>
              <FaArrowLeft /> Back to Dashboard
            </BackButton>
            <TitleSection>
              <FaTruck />
              <div>
                <h1>Create New Shipment</h1>
                <p>
                  All fields are optional - fill in what you have now and
                  complete the rest later
                </p>
              </div>
            </TitleSection>
          </Header>

          {/* Form */}
          <Form onSubmit={handleFormSubmit}>
            {/* Origin Address */}
            <FormSection>
              <SectionTitle>
                <FaMapMarkerAlt /> Origin Address
              </SectionTitle>
              <AddressGrid>
                <FormGroup>
                  <label>Street Address</label>
                  <input
                    type="text"
                    value={formData.originAddress.street}
                    onChange={(e) =>
                      handleAddressChange(
                        "originAddress",
                        "street",
                        e.target.value,
                      )
                    }
                    placeholder="123 Industrial Park"
                  />
                </FormGroup>
                <FormGroup>
                  <label>City</label>
                  <input
                    type="text"
                    value={formData.originAddress.city}
                    onChange={(e) =>
                      handleAddressChange(
                        "originAddress",
                        "city",
                        e.target.value,
                      )
                    }
                    placeholder="Los Angeles"
                  />
                </FormGroup>
                <FormGroup>
                  <label>State</label>
                  <input
                    type="text"
                    value={formData.originAddress.state}
                    onChange={(e) =>
                      handleAddressChange(
                        "originAddress",
                        "state",
                        e.target.value,
                      )
                    }
                    placeholder="CA"
                  />
                </FormGroup>
                <FormGroup>
                  <label>Postal Code</label>
                  <input
                    type="text"
                    value={formData.originAddress.postalCode}
                    onChange={(e) =>
                      handleAddressChange(
                        "originAddress",
                        "postalCode",
                        e.target.value,
                      )
                    }
                    placeholder="90001"
                  />
                </FormGroup>
                <FormGroup>
                  <label>Country</label>
                  <input
                    type="text"
                    value={formData.originAddress.country}
                    onChange={(e) =>
                      handleAddressChange(
                        "originAddress",
                        "country",
                        e.target.value,
                      )
                    }
                    placeholder="USA"
                  />
                </FormGroup>
              </AddressGrid>
            </FormSection>

            {/* Destination Address */}
            <FormSection>
              <SectionTitle>
                <FaMapMarkerAlt /> Destination Address
              </SectionTitle>
              <AddressGrid>
                <FormGroup>
                  <label>Street Address</label>
                  <input
                    type="text"
                    value={formData.destinationAddress.street}
                    onChange={(e) =>
                      handleAddressChange(
                        "destinationAddress",
                        "street",
                        e.target.value,
                      )
                    }
                    placeholder="2-3-4 Chome"
                  />
                </FormGroup>
                <FormGroup>
                  <label>City</label>
                  <input
                    type="text"
                    value={formData.destinationAddress.city}
                    onChange={(e) =>
                      handleAddressChange(
                        "destinationAddress",
                        "city",
                        e.target.value,
                      )
                    }
                    placeholder="Shinjuku"
                  />
                </FormGroup>
                <FormGroup>
                  <label>State</label>
                  <input
                    type="text"
                    value={formData.destinationAddress.state}
                    onChange={(e) =>
                      handleAddressChange(
                        "destinationAddress",
                        "state",
                        e.target.value,
                      )
                    }
                    placeholder="Tokyo"
                  />
                </FormGroup>
                <FormGroup>
                  <label>Postal Code</label>
                  <input
                    type="text"
                    value={formData.destinationAddress.postalCode}
                    onChange={(e) =>
                      handleAddressChange(
                        "destinationAddress",
                        "postalCode",
                        e.target.value,
                      )
                    }
                    placeholder="160-0022"
                  />
                </FormGroup>
                <FormGroup>
                  <label>Country</label>
                  <input
                    type="text"
                    value={formData.destinationAddress.country}
                    onChange={(e) =>
                      handleAddressChange(
                        "destinationAddress",
                        "country",
                        e.target.value,
                      )
                    }
                    placeholder="Japan"
                  />
                </FormGroup>
              </AddressGrid>
            </FormSection>

            {/* Sender Information */}
            <FormSection>
              <SectionTitle>
                <FaUser /> Sender Information
              </SectionTitle>
              <PersonGrid>
                <FormGroup>
                  <label>Name</label>
                  <input
                    type="text"
                    value={formData.sender.name}
                    onChange={(e) =>
                      handlePersonChange("sender", "name", e.target.value)
                    }
                    placeholder="John Smith"
                  />
                </FormGroup>
                <FormGroup>
                  <label>Company</label>
                  <input
                    type="text"
                    value={formData.sender.company}
                    onChange={(e) =>
                      handlePersonChange("sender", "company", e.target.value)
                    }
                    placeholder="Techtronics Industries"
                  />
                </FormGroup>
                <FormGroup>
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.sender.email}
                    onChange={(e) =>
                      handlePersonChange("sender", "email", e.target.value)
                    }
                    placeholder="shipping@example.com"
                  />
                </FormGroup>
                <FormGroup>
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={formData.sender.phone}
                    onChange={(e) =>
                      handlePersonChange("sender", "phone", e.target.value)
                    }
                    placeholder="+1 (555) 123-4567"
                  />
                </FormGroup>
              </PersonGrid>
            </FormSection>

            {/* Receiver Information */}
            <FormSection>
              <SectionTitle>
                <FaUser /> Receiver Information
              </SectionTitle>
              <PersonGrid>
                <FormGroup>
                  <label>Name</label>
                  <input
                    type="text"
                    value={formData.receiver.name}
                    onChange={(e) =>
                      handlePersonChange("receiver", "name", e.target.value)
                    }
                    placeholder="Kenji Tanaka"
                  />
                </FormGroup>
                <FormGroup>
                  <label>Company</label>
                  <input
                    type="text"
                    value={formData.receiver.company}
                    onChange={(e) =>
                      handlePersonChange("receiver", "company", e.target.value)
                    }
                    placeholder="Yamada Electronics"
                  />
                </FormGroup>
                <FormGroup>
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.receiver.email}
                    onChange={(e) =>
                      handlePersonChange("receiver", "email", e.target.value)
                    }
                    placeholder="receiving@example.com"
                  />
                </FormGroup>
                <FormGroup>
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={formData.receiver.phone}
                    onChange={(e) =>
                      handlePersonChange("receiver", "phone", e.target.value)
                    }
                    placeholder="+81 3-1234-5678"
                  />
                </FormGroup>
              </PersonGrid>
            </FormSection>

            {/* Package Details */}
            <FormSection>
              <SectionTitle>
                <FaBox /> Package Details
              </SectionTitle>
              <TwoColumnGrid>
                <FormGroup>
                  <label>Weight</label>
                  <div className="weight-input">
                    <input
                      type="number"
                      step="0.01"
                      value={formData.weight.value}
                      onChange={(e) =>
                        handleWeightChange("value", e.target.value)
                      }
                      placeholder="2450"
                    />
                    <select
                      value={formData.weight.unit}
                      onChange={(e) =>
                        handleWeightChange("unit", e.target.value)
                      }
                    >
                      <option value="kg">kg</option>
                      <option value="lbs">lbs</option>
                    </select>
                  </div>
                </FormGroup>

                <FormGroup>
                  <label>Dimensions</label>
                  <div className="dimensions-input">
                    <input
                      type="number"
                      step="0.01"
                      value={formData.dimensions.length}
                      onChange={(e) =>
                        handleDimensionsChange("length", e.target.value)
                      }
                      placeholder="Length"
                    />
                    <input
                      type="number"
                      step="0.01"
                      value={formData.dimensions.width}
                      onChange={(e) =>
                        handleDimensionsChange("width", e.target.value)
                      }
                      placeholder="Width"
                    />
                    <input
                      type="number"
                      step="0.01"
                      value={formData.dimensions.height}
                      onChange={(e) =>
                        handleDimensionsChange("height", e.target.value)
                      }
                      placeholder="Height"
                    />
                    <select
                      value={formData.dimensions.unit}
                      onChange={(e) =>
                        handleDimensionsChange("unit", e.target.value)
                      }
                    >
                      <option value="cm">cm</option>
                      <option value="inches">inches</option>
                    </select>
                  </div>
                </FormGroup>

                <FormGroup $fullWidth>
                  <label>Description</label>
                  <textarea
                    rows="3"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    placeholder="Describe the shipment contents..."
                  />
                </FormGroup>
              </TwoColumnGrid>
            </FormSection>

            {/* Shipping Details */}
            <FormSection>
              <SectionTitle>
                <FaCalendarAlt /> Shipping Details
              </SectionTitle>
              <TwoColumnGrid>
                <FormGroup>
                  <label>Estimated Days in Transit</label>
                  <input
                    type="number"
                    value={formData.estimatedDaysInTransit}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        estimatedDaysInTransit: e.target.value,
                      })
                    }
                    placeholder="14"
                  />
                </FormGroup>

                <FormGroup>
                  <label>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.target.value })
                    }
                  >
                    <option value="Packaging">Packaging</option>
                    <option value="Shipped">Shipped</option>
                    <option value="In Transit">In Transit</option>
                    <option value="Out for Delivery">Out for Delivery</option>
                    <option value="Delivered">Delivered</option>
                    <option value="Delayed">Delayed</option>
                  </select>
                </FormGroup>

                <FormGroup>
                  <label>Service Type</label>
                  <select
                    value={formData.serviceType}
                    onChange={(e) =>
                      setFormData({ ...formData, serviceType: e.target.value })
                    }
                  >
                    <option value="Standard">Standard</option>
                    <option value="Express">Express</option>
                    <option value="Overnight">Overnight</option>
                    <option value="Freight">Freight</option>
                  </select>
                </FormGroup>
              </TwoColumnGrid>
            </FormSection>

            {/* Pricing */}
            <FormSection>
              <SectionTitle>
                <FaMoneyBillWave /> Pricing
              </SectionTitle>
              <TwoColumnGrid>
                <FormGroup>
                  <label>Shipping Cost ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.shippingCost}
                    onChange={(e) =>
                      setFormData({ ...formData, shippingCost: e.target.value })
                    }
                    placeholder="4500"
                  />
                </FormGroup>

                <FormGroup>
                  <label>Insurance ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.insurance}
                    onChange={(e) =>
                      setFormData({ ...formData, insurance: e.target.value })
                    }
                    placeholder="10000"
                  />
                </FormGroup>
              </TwoColumnGrid>
            </FormSection>

            {/* Additional Options */}
            <FormSection>
              <SectionTitle>
                <FaShieldAlt /> Additional Options
              </SectionTitle>
              <CheckboxGroup>
                <label>
                  <input
                    type="checkbox"
                    checked={formData.isFragile}
                    onChange={(e) =>
                      setFormData({ ...formData, isFragile: e.target.checked })
                    }
                  />
                  <span>This shipment contains fragile items</span>
                </label>
              </CheckboxGroup>

              <FormGroup>
                <label>Special Instructions</label>
                <textarea
                  rows="2"
                  value={formData.specialInstructions}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      specialInstructions: e.target.value,
                    })
                  }
                  placeholder="Any special handling instructions..."
                />
              </FormGroup>
            </FormSection>

            {/* Error and Success Messages */}
            {formError && (
              <ErrorMessage>
                <FaTimes />
                <span>{formError}</span>
              </ErrorMessage>
            )}

            {formSuccess && (
              <SuccessMessage>
                <span>✓</span>
                <span>{formSuccess}</span>
              </SuccessMessage>
            )}

            {/* Form Actions */}
            <FormActions>
              <CancelButton
                type="button"
                onClick={() => navigate("/admin-dashboard")}
              >
                Cancel
              </CancelButton>
              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Creating..." : "Create Shipment"}
                {!isSubmitting && <FaSave />}
              </SubmitButton>
            </FormActions>
          </Form>
        </Container>
      </PageWrapper>

      {/* Nothing is required - this just reminds before creating, never blocks */}
      <ConfirmModal
        open={missingFields.length > 0}
        title="A few fields are still blank"
        message="You haven't filled in the following. You can go back and add them, or continue and create the shipment without them:"
        items={missingFields}
        confirmLabel="Continue Anyway"
        cancelLabel="Go Back & Fill"
        loading={isSubmitting}
        onConfirm={createShipment}
        onClose={() => setMissingFields([])}
      />
    </>
  );
};

// Styled Components
const PageWrapper = styled.div`
  min-height: 100vh;
  background: #f0f2f5;
  padding: 2rem;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: #0b1e33;
  font-size: 0.9rem;
  cursor: pointer;
  margin-bottom: 1rem;
  padding: 0.5rem 0;

  &:hover {
    color: #d4af37;
  }

  svg {
    font-size: 0.8rem;
  }
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  svg {
    font-size: 2rem;
    color: #d4af37;
  }

  h1 {
    font-size: 1.75rem;
    font-weight: 700;
    color: #0b1e33;
    margin: 0 0 0.25rem;
  }

  p {
    font-size: 0.85rem;
    color: #8a9ab0;
    margin: 0;
  }

  @media (max-width: 768px) {
    svg {
      font-size: 1.5rem;
    }

    h1 {
      font-size: 1.3rem;
    }
  }
`;

const Form = styled.form`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);

  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const FormSection = styled.div`
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #e2e8f0;

  &:last-of-type {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 700;
  color: #0b1e33;
  margin-bottom: 1.25rem;

  svg {
    color: #d4af37;
  }
`;

const AddressGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .form-group:first-child {
    grid-column: span 3;

    @media (max-width: 768px) {
      grid-column: span 1;
    }
  }
`;

const PersonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const FormGroup = styled.div`
  label {
    display: block;
    font-size: 0.75rem;
    font-weight: 600;
    color: #4a5568;
    margin-bottom: 0.5rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  input,
  select,
  textarea {
    width: 100%;
    padding: 0.6rem 0.75rem;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 0.85rem;
    font-family: inherit;
    transition: all 0.2s;

    &:focus {
      outline: none;
      border-color: #d4af37;
      box-shadow: 0 0 0 2px rgba(212, 175, 55, 0.1);
    }
  }

  textarea {
    resize: vertical;
  }

  .weight-input {
    display: flex;
    gap: 0.5rem;

    input {
      flex: 2;
    }

    select {
      flex: 1;
    }
  }

  .dimensions-input {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;

    input {
      flex: 1;
      min-width: 70px;
    }

    select {
      width: 80px;
    }
  }
`;

const CheckboxGroup = styled.div`
  margin-bottom: 1rem;

  label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;

    input {
      width: 18px;
      height: 18px;
      cursor: pointer;
    }

    span {
      font-size: 0.85rem;
      color: #4a5568;
    }
  }
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  margin: 1rem 0;

  svg {
    color: #dc2626;
    font-size: 0.9rem;
    flex-shrink: 0;
  }

  span {
    font-size: 0.85rem;
    color: #dc2626;
  }
`;

const SuccessMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #dcfce7;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  margin: 1rem 0;

  span:first-child {
    color: #16a34a;
    font-size: 1rem;
    font-weight: bold;
  }

  span:last-child {
    font-size: 0.85rem;
    color: #16a34a;
  }
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const CancelButton = styled.button`
  padding: 0.7rem 1.5rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8f9fa;
    border-color: #cbd5e0;
  }
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem 1.5rem;
  background: #d4af37;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #c09b28;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

export default CreateShipment;
